// @ts-check
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createClientAction } from '../redux/actions/client-actions';

import { Formik } from 'formik';
import * as Yup from 'yup';
import * as currencies from 'currency-codes/data';

import { makeStyles } from '@material-ui/core/styles';
import { TextField, Button, Grid, CircularProgress, MenuItem, Box } from '@material-ui/core';

import CssBaseline from '@material-ui/core/CssBaseline';
import Container from '@material-ui/core/Container';
import { BackNavigationComponent } from '../../../../core/routes/components/back-navigation-component';
import { Autocomplete } from '@material-ui/lab';
import { formatTimezones } from '../../../../core/utils/timezone-utils';

const CLIENT_PLANS = ['STARTER', 'CORE', 'PROFESSIONAL', 'ENTERPRISE'];

const useStyles = makeStyles((theme) => ({
  root: {
    '& > *': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1)
    },
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2em'
  },
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  sectionHeading: {
    margin: theme.spacing(2)
  },
  margin: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  formButtons: {
    marginTop: theme.spacing(3)
  },
  errorText: {
    color: '#f44336',
    textAlign: 'center',
    display: 'block',
    margin: theme.spacing(3)
  }
}));

function CreateClientForm(props) {
  const classes = useStyles();
  const history = useHistory();
  const { error, loading } = useSelector((state) => state.client);

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    timezones
  } = props;

  /**
   * @param {string} name
   */
  const formikProps = (name, initialValue = '') => ({
    name: name,
    value: typeof values[name] !== 'undefined' ? values[name] : initialValue,
    onChange: handleChange,
    onBlur: handleBlur,
    error: touched[name] && Boolean(errors[name]),
    helperText: touched[name] ? errors[name] : ''
  });

  return (
    <form className={classes.root} noValidate autoComplete="off" onSubmit={handleSubmit}>
      <TextField
        {...formikProps('name')}
        id="outlined-full-width"
        label="Name"
        type="text"
        variant="outlined"
        fullWidth={true}
      />

      <TextField
        {...formikProps('organization')}
        id="outlined-full-width"
        label="Organization's Unique Name"
        type="text"
        variant="outlined"
        fullWidth={true}
      />

      <TextField
        select
        {...formikProps('client_type')}
        id="outlined-full-width"
        value={values['client_type']}
        label="Client Type"
        onChange={(e) => {
          setFieldValue('client_type', e.target.value);
        }}
        variant="outlined"
        fullWidth={true}
      >
        <MenuItem value={'ON_DEMAND'}>On-Demand</MenuItem>
        <MenuItem value={'LOGISTICS'}>Logistics</MenuItem>
      </TextField>

      <TextField
        select
        {...formikProps('plan')}
        id="outlined-full-width"
        label="Client Plan"
        onChange={(e) => {
          setFieldValue('plan', e.target.value);
        }}
        onBlur={handleBlur}
        fullWidth={true}
        variant="outlined"
      >
        {CLIENT_PLANS.map((plan) => (
          <MenuItem key={plan} value={plan}>
            {plan}
          </MenuItem>
        ))}
      </TextField>

      <Autocomplete
        id="outlined-full-width"
        disablePortal
        onChange={(e, option) => {
          setFieldValue('timezone', option?.name);
        }}
        onBlur={handleBlur}
        options={timezones}
        getOptionLabel={(option) => option.display}
        getOptionSelected={(option, value) => option.name === value.name}
        fullWidth={true}
        renderInput={(params) => (
          <TextField
            error={touched['timezone'] && Boolean(errors['timezone'])}
            helperText={touched['timezone'] ? errors['timezone'] : ''}
            {...params}
            fullWidth={true}
            variant="outlined"
            label="Client Timezone"
          />
        )}
      ></Autocomplete>

      <Autocomplete
        id="outlined-full-width"
        disablePortal
        onChange={(e, data) => {
          if (data) {
            const { code } = data;
            setFieldValue('currency', code);
          } else setFieldValue('currency', undefined);
        }}
        onBlur={handleBlur}
        options={currencies}
        getOptionLabel={(option) => `${option.currency} - (${option.code})`}
        getOptionSelected={(option, value) => option.code === value.code}
        fullWidth={true}
        renderInput={(params) => (
          <TextField
            error={touched['currency'] && Boolean(errors['currency'])}
            helperText={touched['currency'] ? errors['currency'] : ''}
            {...params}
            variant="outlined"
            label="Currency"
          />
        )}
      ></Autocomplete>

      <div className={classes.formButtons}>
        {!loading && (
          <>
            <Button
              className={classes.margin}
              type="submit"
              variant="contained"
              size="large"
              color="primary"
              fullWidth={true}
            >
              Create Client
            </Button>

            <Button
              className={classes.margin}
              variant="outlined"
              size="large"
              color="secondary"
              fullWidth={true}
              onClick={() => history.push('/clients')}
            >
              Cancel
            </Button>
          </>
        )}

        {loading && (
          <Button
            className={classes.margin}
            variant="contained"
            size="large"
            color="primary"
            fullWidth={true}
          >
            <CircularProgress color="inherit" size={26} />
          </Button>
        )}

        {error && (
          <div>
            <span className={classes.errorText}>{error.message}</span>
          </div>
        )}
      </div>
    </form>
  );
}

const validationSchema = Yup.object({
  name: Yup.string('Enter client name').required('Client name is required'),
  organization: Yup.string('Enter unique organization name')
    .trim()
    .required('Organization name is required'),
  timezone: Yup.string('Enter Client timezone').nullable().required('Client timezone is required'),
  client_type: Yup.mixed().oneOf(['ON_DEMAND', 'LOGISTICS']).required('Client Type is required'),
  currency: Yup.string('Enter Client currency').nullable().required('Client currency is required'),
  plan: Yup.string('Enter Client Plan').nullable().required('Client plan is required')
});

function CreateClientFormValidation() {
  const history = useHistory();
  const dispatch = useDispatch();
  const timezones = formatTimezones();

  const values = {
    name: '',
    organization: '',
    client_type: '',
    timezone: undefined,
    currency: undefined,
    plan: undefined
  };

  const submit = (data, actions) => {
    console.log(data);
    const props = { history };
    dispatch(createClientAction(data, props));
  };

  return (
    <Formik initialValues={values} validationSchema={validationSchema} onSubmit={submit}>
      {(props) => <CreateClientForm {...props} timezones={timezones} />}
    </Formik>
  );
}

export function CreateClientPage() {
  const classes = useStyles();

  return (
    <React.Fragment>
      <CssBaseline />
      <Container className={classes.container}>
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} md={8}>
            <BackNavigationComponent
              page="Create client"
              uri="/clients"
              path={[
                {
                  page: 'Home',
                  uri: '/'
                },
                {
                  page: 'Clients',
                  uri: '/clients'
                }
              ]}
            />

            <CreateClientFormValidation />
          </Grid>
        </Grid>
      </Container>
    </React.Fragment>
  );
}
